import { FileExcelOutlined, FilePdfOutlined, PlusOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Menu,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import { deletePackingMode, fetchPackingModeList, resetPackingModeData} from '../../slices/packing';
import ImportDataModal from '../../Common/ImportDataModal';
import moment from 'moment';
import { debounce } from 'lodash';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import { createActivityLog } from '../../slices/activity-log';
import logo from '../../Images/alux-logo.png';
import formatFile from "../../Images/sample-files/packing-format.xlsx";

function PackingList() {
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { user: currentUser } = useSelector(state => state.auth);
  const [search, setSearch] = useState(
    localStorage.getItem('die_text_search_alloy') || '',
  );
  const [inputValue, setInputValue] = useState(
    localStorage.getItem('die_text_search_alloy') || '',
  );
  const [groupSearch, setGroupSearch] = useState(() => {
    const saved = localStorage.getItem('die_group_search_alloy');
    return saved ? JSON.parse(saved) : null;
  });

  const ipAddress = useSelector(state => state.ip.ipAddress);
  useEffect(() => {
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [ipAddress, dispatch]);

  const { data = [], totalRecord, loading } = useSelector(state => state.packingMode);

  useEffect(() => {
    dispatch(resetPackingModeData());
    dispatch(
      fetchPackingModeList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [page, pagesize, search, orderBy]);

  const resetFilter = () => {
    setSearch('');
    setGroupSearch('');
    setInputValue('');
    setOrderBy(null); // if applicable
    localStorage.removeItem('die_text_search_alloy');
    localStorage.removeItem('die_group_search_alloy');
    dispatch(fetchPackingModeList({ page: 1, pagesize }));
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'name' && sortOrder === 'ascend') {
      sortingEnumKey = 'name';
    }
    if (sortField === 'name' && sortOrder === 'descend') {
      sortingEnumKey = '-name';
    }

    return sortingEnumKey;
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  };

  const handleSearch = e => {
    const value = e.target.value;
    debouncedAPICall(value);
    setInputValue(value);
    setSearch(value);
    localStorage.setItem('die_text_search_alloy', value);
  };
  const debouncedAPICall = useMemo(
    () =>
      debounce(query => {
        if (query.length >= 3) {
          dispatch(fetchPackingModeList({ search: query }));
          localStorage.setItem('die_text_search_alloy', query);
        }
      }, 100),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedAPICall.cancel();
    };
  }, [debouncedAPICall]);
  const refreshTable = () => {
    dispatch(resetPackingModeData());
    dispatch(
      fetchPackingModeList({
        page: 1,
        pagesize,
        ...(search && { search }),
        ...(orderBy && { ordering: orderBy }),
      })
    );
  };

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(deletePackingMode(id))
      .unwrap()
      .then(() => {
        message.success('Packing Mode Archive Successfully!');
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }

          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error); // Handle string error
        } else if (error instanceof Error) {
          message.error(error.message); // Handle Error object
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const allColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo', // Serial number
      key: 'SrNo',
      sorter: false,
      render: (text, record, index) => {
        return (page - 1) * pagesize + index + 1;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: false,
      render: (text) => (
        <div
          // WARNING: sanitize `text` if it comes from users/external sources
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? `${record.first_name} ${record.last_name}` : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: record => {
        return (
          <span>
            {record && record.trim()
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record && record.first_name?.trim() && record.last_name?.trim()
              ? `${record.first_name} ${record.last_name}`
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'action',
      render: (text, record) => (
        <Space>
          {currentUser.permissions.permissions.product.includes(
            'change_alloy',
          ) && (
              <Link to={`/edit-packing/${record?.id}`}>
                <Tooltip title="Edit">
                  <EditIcon />
                </Tooltip>
              </Link>
            )}
          {currentUser.permissions.permissions.product.includes(
            'delete_alloy',
          ) && (
              <Link>
                <Tooltip placement="top" title={'Archive'}>
                  <Popconfirm
                    title="Are you sure you want to archive this record?"
                    onConfirm={() => handleDelete(record.id)} // Trigger delete on confirmation
                    onCancel={() => message.info('Action cancelled')} // Optional cancel action
                    okText="Yes"
                    cancelText="No"
                  >
                    <Archive style={{ cursor: 'pointer' }} />
                  </Popconfirm>
                </Tooltip>
              </Link>
            )}
        </Space>
      ),
    },
  ];

  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'name',
    'description',
    'action',
  ]);

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const filteredColumns = allColumns.filter(column =>
    checkedList.includes(column.key),
  );

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
          onClick={e => e.stopPropagation()}
        >
          <Row>
            <Col span={12}>
              <Checkbox value="SrNo" disabled>
                Sr. No.
              </Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="name">Name</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="description">Description</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  const csvheaders = [
    { label: 'Sr. No', key: 'SrNo' },
    { label: 'Name', key: 'name' },
    { label: 'Description', key: 'description' },
  ];

  const csvFilterheaders = csvheaders.filter(column =>
    checkedList.includes(column.key),
  );

  const csvData = data?.map((item, index) => ({
    SrNo: index + 1,
    name: item.name,
    description: item.description,
    created_at: item.created_at
      ? moment(item.created_at).format('DD-MM-YYYY')
      : 'N/A',
    created_by: item.created_by
      ? `${item.created_by.first_name} ${item.created_by.last_name}`
      : 'N/A',
    updated_at: item.updated_at
      ? moment(item.updated_at).format('DD-MM-YYYY')
      : 'N/A',
    updated_by: item.updated_by
      ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
      : 'N/A',
  }));

  const handlePdfDownload = async () => {
    const pdf = new jsPDF('l', 'pt', 'a4');
    const pdfColumns = [
      { header: 'Sr No.', dataKey: 'SrNo' },
      { header: 'Name', dataKey: 'name' },
      { header: 'Description', dataKey: 'description' },
      { header: 'created At', dataKey: 'created_at' },
      { header: 'created By', dataKey: 'created_by' },
      { header: 'Updated At', dataKey: 'updated_at' },
      { header: 'Updated By', dataKey: 'updated_by' },
    ];

    const pdffilteredColumns = pdfColumns.filter(column =>
      checkedList.includes(column.dataKey),
    );

    const htmlToBullets = (html) => {
      return html
        .replace(/<\/li>/g, "\n")
        .replace(/<li>/g, "• ")
        .replace(/<[^>]*>/g, ""); // remove remaining tags
    };

    const rows = data.map((item, index) => ({
      SrNo: index + 1,
      name: item.name || 'N/A',
      description: item?.description ? htmlToBullets(item.description) : 'N/A',
      created_at: item.created_at
        ? moment(item.created_at).format('DD-MM-YYYY')
        : 'N/A',
      created_by: item.created_by
        ? `${item.created_by.first_name} ${item.created_by.last_name}`
        : 'N/A',
      updated_at: item.updated_at
        ? moment(item.updated_at).format('DD-MM-YYYY')
        : 'N/A',
      updated_by: item.updated_by
        ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
        : 'N/A',
    }));

    const pageWidth = pdf.internal.pageSize.width;
    const pageHeight = pdf.internal.pageSize.height;

    // Heading
    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);
    const headingText = 'Packing Mode List';
    const headingX = pageWidth / 2 - pdf.getTextWidth(headingText) / 2;
    pdf.text(headingText, headingX, 40);

    pdf.autoTable({
      columns: pdffilteredColumns,
      body: rows,
      startY: 65,
      theme: 'grid',
      styles: {
        font: 'times',
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 3.5,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      rowStyles: {
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      tableLineColor: [0, 0, 0],
      rowPageBreak: 'avoid',
      didDrawPage: data => {
        const pageNumber = pdf.internal.getCurrentPageInfo().pageNumber;
        const totalPages = pdf.internal.getNumberOfPages();

        // ✅ Logo only on the first page
        if (pageNumber === 1) {
          pdf.addImage(logo, 'PNG', pageWidth - 100, 10, 90, 30);
        }

        // ✅ Footer (Date & Time left, Page Number right)
        const currentDate = moment().format('DD-MM-YYYY HH:mm');
        pdf.setFontSize(10);
        pdf.text(currentDate, 40, pageHeight - 20); // left footer
        pdf.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 100, pageHeight - 20); // right footer
      },
    });

    pdf.save(`packing-mode.pdf`);

    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Packing Mode',
        ip_address: ipAddress,
        discription: 'Packing Mode pdf download successfully!',
      }),
    );
  };

  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Packing Mode',
        ip_address: ipAddress,
        discription: 'Packing Mode excel download successfully!',
      }),
    );
  };

  return (
    <div className='mainwrap'>
      <div className="sticyaction">
        <Row
          className="pagenamerow mb-0"
          justify="space-between"
          align="middle"
        >
          <Col>
            <h2 style={{ margin: 0 }}>Packing Mode</h2>
            <div className="bredcrumbwrp">
              <Link to="/customer-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  //{ title: <Link to="/customer-list">Customers</Link> },
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Packing Mode' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                onChange={handleSearch}
                style={{ width: 200 }}
                value={search}
              />

              <Tooltip placement="top" title="Reset Filter">
                <Button
                  type="primary"
                  disabled={!search && !orderBy}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
              {currentUser.permissions.permissions.product.includes(
                'download_alloy_excel_copy',
              ) && (
                  <Tooltip placement="top" title={'Export Excel'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      style={{ width: '10px' }}
                      onClick={activityLog}
                    >
                      <CSVLink
                        filename={'packing-mode.csv'}
                        className="iconlink"
                        style={{ width: '10px' }}
                        data={csvData}
                        headers={csvFilterheaders}
                      >
                        <FileExcelOutlined />
                      </CSVLink>
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.product.includes(
                'download_alloy_pdf_copy',
              ) && (
                  <Tooltip placement="top" title={'Export PDF'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      onClick={handlePdfDownload}
                    >
                      <FilePdfOutlined />
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.product.includes(
                'add_alloy',
              ) && (
                  <Link to="/add-packing">
                    <Tooltip title=" Add Packing Mode">
                      <Button type="primary" icon={<PlusOutlined />}>
                        Add Packing
                      </Button>
                    </Tooltip>
                  </Link>
                )}
              <Button type="primary" icon={<UploadOutlined />} onClick={() => setOpenModal(true)}>
                Import Data
              </Button>
              {currentUser.permissions.permissions.product.includes(
                'delete_alloy',
              ) && (
                  <Tooltip placement="top" title="Archive">
                    <Link to="/archive-packing-mode" className="iconlink">
                      {' '}
                      <Archiveicon style={{ cursor: 'pointer' }} />
                    </Link>
                  </Tooltip>
                )}
              <Link>
                <Tooltip placement="topLeft" title="Column Filter Options">
                  <Dropdown
                    overlay={columnMenu}
                    trigger={['click']}
                    placement="bottomRight"
                    visible={dropdownVisible}
                    onVisibleChange={setDropdownVisible}
                  >
                    <Button
                      type="primary"
                      icon={<ColumnList />}
                      onClick={e => e.preventDefault()}
                    />
                  </Dropdown>
                </Tooltip>
              </Link>
            </Space>
          </Col>
        </Row>
      </div>
      <ImportDataModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        refreshTable={refreshTable}
        formatFilePath={formatFile}
        model={"packingmode"}
      />
      <div className='tablewrp'>
        <Table
          rowKey="id"
          columns={filteredColumns}
          dataSource={data}
          pagination={false} // Disable built-in pagination
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: '600px' }}
        />
      </div>

      <Pagination
        current={page}
        pageSize={pagesize}
        total={totalRecord}
        showSizeChanger
        onShowSizeChange={handlePageSizeChange}
        pageSizeOptions={['20', '50', '100']}
        showTotal={total => `Total ${total} items`}
        onChange={handlePageChange}
        locale={{ items_per_page: 'Items / Page' }}
      />

    </div>
  );
}

export default PackingList;
